// PlatformDetailPage — Trading platform detail with tier-gating
// Replaces StockDetailPage (since we no longer trade stocks)
// Layout:
//   - Top nav: < Back | "{Platform}" | share icon
//   - Pill tabs: Overview | Requirements | Bots | Docs
//   - Platform header (icon + name + tier required)
//   - Big chart of platform volume
//   - Description + features
//   - Tier required (if locked, show "Upgrade" CTA)
//   - Bot strategies compatible with this platform

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PLATFORMS, BOT_STRATEGIES, TIERS, type TierKey, DEMO_CHALLENGE } from '../../data/platforms';
import { PlatformLogo, TierPill } from '../../components/Brand';

const TABS = ['Overview', 'Requirements', 'Bots', 'Docs'] as const;
type Tab = typeof TABS[number];
const RANGES = ['1D', '1W', '1M', '3M', '6M', '1Y'] as const;
type RangeKey = typeof RANGES[number];

export function PlatformDetailPage() {
  const { key, stockId } = useParams();
  const nav = useNavigate();
  const platformKey = key || stockId || 'Deepchart';
  const platform = PLATFORMS.find(p => p.key === platformKey) || PLATFORMS[0];
  const [tab, setTab] = useState<Tab>('Overview');
  const [range, setRange] = useState<RangeKey>('1M');

  const userTier: TierKey = DEMO_CHALLENGE.tier;
  const tierOrder = { Beginner: 0, Mid: 1, Professional: 2 } as const;
  const locked = tierOrder[userTier] < tierOrder[platform.tierRequired];
  const requiredTier = TIERS[platform.tierRequired];

  // Chart data (simulated platform volume)
  const points = (() => {
    const n = 30;
    const out: number[] = [];
    let v = 100;
    for (let i = 0; i < n; i++) {
      v += Math.sin(i * 0.3) * 8 + Math.random() * 4;
      out.push(v);
    }
    return out;
  })();

  const compatibleBots = BOT_STRATEGIES.filter(s => platform.botCompatibility.includes(s.key));

  return (
    <div className="min-h-screen bg-white">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-text-1">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">{platform.displayName}</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-text-1">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" strokeLinecap="round" /></svg>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-tf-border bg-white px-3 pt-2">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`platform-tab-${t.toLowerCase()}`}
              className={['whitespace-nowrap rounded-full px-4 py-1.5 text-[14px] font-semibold transition',
                tab === t ? 'bg-white border border-tf-text-1 text-tf-text-1' : 'text-tf-text-3 hover:bg-tf-surface-1'].join(' ')}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {tab === 'Overview' && (
        <>
          {/* Hero with platform logo + tier required */}
          <div className="px-4 pt-5">
            <div className="flex items-center gap-3">
              <PlatformLogo name={platform.key} size={56} />
              <div>
                <h1 className="text-[22px] font-bold tracking-tight text-tf-text-1">{platform.displayName}</h1>
                <div className="mt-0.5 flex items-center gap-2">
                  {platform.badge && <span className="rounded-full bg-tf-warning px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white">{platform.badge}</span>}
                  <span className="rounded-full bg-tf-surface-3 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-tf-text-3">{platform.category}</span>
                </div>
              </div>
            </div>

            {/* Tier requirement */}
            <div className="mt-4 flex items-center justify-between rounded-[14px] border border-tf-border bg-tf-surface-3 p-3">
              <div className="flex items-center gap-2">
                <span className="text-[13px] text-tf-text-3">Required tier</span>
                <TierPill tier={platform.tierRequired} />
              </div>
              {locked ? (
                <button onClick={() => nav('/portal/tiers')} className="rounded-full bg-tf-text-1 px-3 py-1.5 text-[12px] font-semibold text-white">
                  Upgrade
                </button>
              ) : (
                <span className="text-[12px] font-semibold text-tf-profit">✓ Unlocked</span>
              )}
            </div>

            {/* Description */}
            <p className="mt-4 text-[14px] leading-6 text-tf-text-1">{platform.description}</p>

            {/* Features */}
            <h2 className="mt-5 text-[16px] font-semibold text-tf-text-1">Features</h2>
            <ul className="mt-2 space-y-1.5">
              {platform.bullets.map((b, i) => (
                <li key={i} className="flex items-center gap-2 text-[14px] text-tf-text-1">
                  <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-tf-profit" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  {b}
                </li>
              ))}
            </ul>

            {/* Volume chart */}
            <h2 className="mt-5 text-[16px] font-semibold text-tf-text-1">Platform activity</h2>
            <div className="mt-2 -mx-2">
              <VolumeChart points={points} color="#007AFF" />
            </div>
            <div className="mt-2 flex items-center justify-between px-1">
              {RANGES.map((r) => (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  data-testid={`range-${r}`}
                  className={['rounded-full px-3 py-1 text-[13px] font-semibold',
                    r === range ? 'bg-tf-text-1 text-white' : 'text-tf-text-3'].join(' ')}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {tab === 'Requirements' && (
        <div className="px-4 pt-4">
          <h2 className="text-[16px] font-semibold text-tf-text-1">System requirements</h2>
          <div className="mt-3 overflow-hidden rounded-[16px] border border-tf-border bg-white">
            <Stat label="Minimum tier" value={<TierPill tier={platform.tierRequired} />} />
            <Stat label="Account size" value="Any" />
            <Stat label="Trading instruments" value="Futures (ES, NQ, YM, RTY, CL, GC)" />
            <Stat label="Recommended brokers" value="Any Rithmic-compatible" />
            <Stat label="Setup time" value="~5 minutes" />
            <Stat label="License" value="Included with active challenge" last />
          </div>

          <h2 className="mt-6 text-[16px] font-semibold text-tf-text-1">Compatibility</h2>
          <div className="mt-3 overflow-hidden rounded-[16px] border border-tf-border bg-white">
            <div className="px-4 py-3 border-b border-tf-border flex items-center justify-between">
              <span className="text-[14px] text-tf-text-3">Browser</span>
              <span className="text-[14px] font-medium text-tf-profit">✓ Web (all modern)</span>
            </div>
            <div className="px-4 py-3 border-b border-tf-border flex items-center justify-between">
              <span className="text-[14px] text-tf-text-3">Windows desktop</span>
              <span className="text-[14px] font-medium text-tf-profit">✓ Yes</span>
            </div>
            <div className="px-4 py-3 border-b border-tf-border flex items-center justify-between">
              <span className="text-[14px] text-tf-text-3">macOS</span>
              <span className="text-[14px] font-medium text-tf-profit">✓ Yes</span>
            </div>
            <div className="px-4 py-3 flex items-center justify-between">
              <span className="text-[14px] text-tf-text-3">API access</span>
              <span className={['text-[14px] font-medium', platform.tierRequired === 'Professional' ? 'text-tf-profit' : 'text-tf-text-3'].join(' ')}>
                {platform.tierRequired === 'Professional' ? '✓ Yes' : '🔒 Pro tier'}
              </span>
            </div>
          </div>
        </div>
      )}

      {tab === 'Bots' && (
        <div className="px-4 pt-4">
          <h2 className="text-[16px] font-semibold text-tf-text-1">Compatible AI bots</h2>
          <p className="mt-1 text-[13px] text-tf-text-3">Bots that can trade this platform automatically.</p>
          <div className="mt-3 space-y-2">
            {compatibleBots.map((b) => {
              const botLocked = tierOrder[userTier] < tierOrder[b.tierRequired];
              return (
                <button
                  key={b.key}
                  onClick={() => !botLocked && nav('/portal/bot?strategy=' + b.key)}
                  data-testid={`bot-strategy-${b.key}`}
                  className={['flex w-full items-center gap-3 rounded-[14px] border p-3 text-left transition',
                    botLocked ? 'border-tf-border bg-white opacity-60' : 'border-tf-border bg-white active:scale-[0.99]'].join(' ')}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-[10px] text-[20px]" style={{ backgroundColor: b.color + '15' }}>
                    {b.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[14px] font-semibold text-tf-text-1">{b.name}</span>
                      {botLocked && <span className="rounded-full bg-tf-text-1 px-1.5 py-0.5 text-[9px] font-semibold text-white">🔒 {b.tierRequired}</span>}
                    </div>
                    <div className="text-[12px] text-tf-text-3 truncate">{b.tagline}</div>
                  </div>
                  {!botLocked && <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'Docs' && (
        <div className="px-4 pt-4">
          <h2 className="text-[16px] font-semibold text-tf-text-1">Documentation</h2>
          <div className="mt-3 overflow-hidden rounded-[16px] border border-tf-border bg-white">
            <DocItem title="Getting started" sub="Connect your broker in 5 min" />
            <DocItem title="Volume profile & TPO" sub="How to read the chart" />
            <DocItem title="Order flow setup" sub="Configure DOM, footprint, icebergs" />
            <DocItem title="Risk management" sub="Set position size, stops, alerts" />
            <DocItem title="Keyboard shortcuts" sub="Trade faster with hotkeys" last />
          </div>
        </div>
      )}

      {/* Bottom action — Activate this platform */}
      {!locked && (
        <div className="fixed inset-x-0 bottom-0 z-10 border-t border-tf-border bg-white/95 backdrop-blur-xl" style={{ paddingBottom: 'calc(56px + env(safe-area-inset-bottom))' }}>
          <div className="mx-auto max-w-[640px] px-4 py-3 md:px-6">
            <button
              onClick={() => nav('/portal/challenge/tier?platform=' + platform.key)}
              data-testid="activate-platform"
              className="flex h-[52px] w-full items-center justify-center rounded-full bg-tf-text-1 text-[16px] font-semibold text-white active:scale-[0.99]"
            >
              Activate {platform.displayName}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function VolumeChart({ points, color }: { points: number[]; color: string }) {
  if (points.length < 2) return null;
  const w = 360, h = 80;
  const min = Math.min(...points), max = Math.max(...points) - min || 1;
  const path = points.map((p, i) => {
    const x = (i / (points.length - 1)) * w;
    const y = h - ((p - min) / max) * (h - 20) - 10;
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
  const fillPath = `${path} L${w} ${h} L0 ${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="h-20 w-full">
      <defs>
        <linearGradient id="vol-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fillPath} fill="url(#vol-fill)" />
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Stat({ label, value, last }: { label: string; value: React.ReactNode; last?: boolean }) {
  return (
    <div className={['flex items-center justify-between px-4 py-3', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <span className="text-[14px] text-tf-text-3">{label}</span>
      <span className="text-[14px] font-medium text-tf-text-1">{value}</span>
    </div>
  );
}

function DocItem({ title, sub, last }: { title: string; sub: string; last?: boolean }) {
  return (
    <button className={['flex w-full items-center justify-between border-tf-border px-4 py-3 text-left active:bg-tf-surface-1', last ? '' : 'border-b'].join(' ')}>
      <div>
        <div className="text-[14px] font-semibold text-tf-text-1">{title}</div>
        <div className="text-[12px] text-tf-text-3">{sub}</div>
      </div>
      <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </button>
  );
}
